import { Button, Card, Divider, Icons, Stack, styled, Tooltip, Typography } from '@healthinal/ui';
import { differenceInMilliseconds } from 'date-fns';
import { useState } from 'react';
import { TherapyComplianceDto, TherapyComplianceDtoTherapyCompliance } from '../../../../../api/generated/model';
import { getTherapyComplianceStatusText } from '../../../../../utils/compliance.ts';
import { AddComplianceDialog } from './AddComplianceDialog.tsx';
import { ConditionComplianceCardRow } from './ConditionComplianceCardRow.tsx';

const COMPLIANCE_LIST_MAX_HEIGHT = 438;

export function ComplianceCard({
  compliance,
  patientId,
  therapyId,
}: {
  compliance?: TherapyComplianceDto[] | null;
  patientId: string;
  therapyId?: string | null;
}) {
  const [isAddComplianceDialogOpen, setIsAddComplianceDialogOpen] = useState(false);
  const [editingCompliance, setEditingCompliance] = useState<TherapyComplianceDto | null>(null);

  const isComplianceDialogOpen = isAddComplianceDialogOpen || editingCompliance !== null;

  const handleCloseComplianceDialog = () => {
    setIsAddComplianceDialogOpen(false);
    setEditingCompliance(null);
  };
  return (
    <>
      <Card>
        <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
          <Typography level="title-lg">Compliance</Typography>
          {compliance && compliance?.length > 0 && (
            <Button color={'neutral'} onClick={() => setIsAddComplianceDialogOpen(true)}>
              Compliance erfassen
            </Button>
          )}
        </Stack>

        <Divider />
        {compliance?.length ? (
          <Stack
            gap={2}
            sx={{
              maxHeight: COMPLIANCE_LIST_MAX_HEIGHT,
              overflowY: 'auto',
              overflowX: 'hidden',
              pr: 0.5,
            }}>
            {[...compliance]
              .sort((a, b) => differenceInMilliseconds(b.date, a.date))
              .map((complianceItem) => (
                <ConditionComplianceCardRow
                  key={complianceItem.id ?? complianceItem.date}
                  showDanger={complianceItem.therapyCompliance === TherapyComplianceDtoTherapyCompliance.NO}
                  showStatusIcon
                  date={complianceItem.date}
                  createdBy={complianceItem.createdBy}
                  status={getTherapyComplianceStatusText(complianceItem.therapyCompliance)}
                  fileDetails={complianceItem.fileDetails}
                  onEdit={() => setEditingCompliance(complianceItem)}
                />
              ))}
          </Stack>
        ) : (
          <Stack alignItems={'center'} gap={1} p={3}>
            <Icons.Description />
            <Typography level="body-sm">Noch keine Compliance vorhanden.</Typography>
            <Tooltip
              disableHoverListener={therapyId !== undefined}
              title={'Um eine Compliance erfassen zu können muss eine Therapie vorhanden sein.'}>
              <StyledButton
                variant={!therapyId ? 'soft' : 'solid'}
                color={!therapyId ? 'neutral' : 'primary'}
                sx={!therapyId ? { cursor: 'default' } : { cursor: 'pointer' }}
                onClick={() => therapyId && setIsAddComplianceDialogOpen(true)}>
                Compliance erfassen
              </StyledButton>
            </Tooltip>
          </Stack>
        )}
      </Card>
      <AddComplianceDialog
        open={isComplianceDialogOpen}
        onClose={handleCloseComplianceDialog}
        patientId={patientId}
        currentCompliance={editingCompliance ?? undefined}
      />
    </>
  );
}

const StyledButton = styled(Button)(({ theme }) => ({
  '--variant-softHoverBg': theme.palette.neutral.softBg,
  '--variant-softActiveBg': theme.palette.neutral.softBg,
  '--variant-softActiveColor': theme.palette.neutral.softColor,
}));
